<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { inject, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { KsIcon } from '@aschehoug/kloss-2'
import { KsButton, KsSpinner } from '@aschehoug/kloss'
import useAuthStore from '~/stores/auth'
import { useFileIcon } from '~/composables/useFileIcon'
import { apiClient } from '~/api/client/apiClient'

const { file } = defineProps<{ file: { title: string, url: string } }>()

const emit = defineEmits(['deleteFile'])

const { t } = useI18n()
const { getFileIcon } = useFileIcon()
const { isTeacher, isStudent } = storeToRefs(useAuthStore())

const toast = inject('ksToast') as Toast

const isLoading = ref(false)

const downloadFile = async () => {
  try {
    isLoading.value = true
    const response = (await apiClient.post(
      '/file/sign',
      { key: file.url },
    )).data
    if (!response)
      return
    const link = document.createElement('a')
    link.href = response.uri
    link.click()
    link.remove()
  } catch (error) {
    console.error('Error downloading file:', error)
    toast.error(t('formWriter.toasts.fileDownloadError'))
  } finally {
    isLoading.value = false
    toast.success(t('formWriter.toasts.fileDownloadSuccess'))
  }
}
</script>

<template>
  <div class="space-y-2">
    <p class="text-lg font-medium" v-text="isTeacher ? t('formWriter.fileTeacher') : t('formWriter.fileStudent')" />
    <div class="flex items-center justify-between gap-4 rounded-xl bg-[--theme-pop] p-5 text-[--theme-60]">
      <div class="flex items-center gap-5">
        <KsIcon :icon="getFileIcon(file.url)" class="text-4xl" />
        <p class="text-base font-bold leading-tight" v-text="file.title" />
      </div>
      <div class="flex gap-2">
        <KsButton
          variant="primary"
          size="small"
          :icon-left="isLoading ? '' : 'download'"
          @click="downloadFile"
        >
          <KsSpinner v-if="isLoading" size="small" />
          {{ isLoading ? t('formWriter.downloading') : t('formWriter.download') }}
        </KsButton>
        <KsButton
          v-if="isStudent"
          variant="secondary"
          size="small"
          icon-left="trash"
          @click="emit('deleteFile')"
        >
          {{ t('formWriter.delete') }}
        </KsButton>
      </div>
    </div>
  </div>
</template>
